import { Link, Modal, Stack } from '@mui/material'
import type { Definition, Organization } from '../../../types'
import RedLine from '../images/red-squiggly-line.svg'
import closeIcon from '../images/close-icon.svg'
import ModalChipRow from './ModalChipRow'
import State from './State'
import {
  STRATEGY_PARENT_OPTIONS,
  TARGET_PARENT_OPTIONS,
} from '../../../constants'

interface DetailsModalProps {
  organization: Organization
  definitions: Definition[]
  cityOrganizations: Organization[]
  onOrganizationClick: (
    organization: Organization,
    organizations: Organization[]
  ) => void
  onCloseModal: () => void
}

const DetailsModal = ({
  organization,
  definitions,
  cityOrganizations,
  onCloseModal,
  onOrganizationClick,
}: DetailsModalProps) => {
  const {
    geo_scope,
    focus,
    strategy,
    target,
    stage,
    repair,
    initiative_type,
    budget_amount,
    budget_date,
    value_amount,
    value_date,
  } = organization
  const currentIndex = cityOrganizations.findIndex(
    (org) => org.org_name === organization.org_name
  )
  const isInitiative = organization.initCommTask === true
  return (
    <Modal
      open
      onClose={onCloseModal}
      aria-labelledby="organization-details"
      aria-describedby="a detailed view of an organization"
    >
      <div
        className="absolute w-[991px] max-h-[676px] top-[50%] left-[50%] bg-details-modal-bg p-16 translate-x-[-50%] translate-y-[-50%] overflow-scroll focus-visible:outline-none bg-bottom"
        style={{
          minHeight: `min(calc(100vh - 64px), 676px)`,
        }}
      >
        <img
          src={closeIcon}
          alt="the letter x"
          role="button"
          className="absolute top-0 right-0 cursor-pointer m-6"
          onClick={onCloseModal}
        />
        <Stack direction="row" className="gap-12">
          <Stack className="gap-8">
            <h2 className="text-[26px] leading-[27px] font-decimal font-bold text-green">
              {organization.org_name}
            </h2>
            <Stack direction="row" className="gap-4">
              <Stack className="gap-4">
                {!isInitiative ? (
                  <>
                    <ModalChipRow
                      title="Geo Scope"
                      labels={(geo_scope || []).map(
                        (opt) =>
                          definitions.find((def) => {
                            return `geo_scope:${opt}` === def.id
                          })?.display_label || ''
                      )}
                      definitions={definitions}
                    />
                    <ModalChipRow
                      title="Focus Area"
                      labels={(focus || []).map(
                        (opt) =>
                          definitions.find((def) => {
                            return `focus:${opt}` === def.id
                          })?.display_label || ''
                      )}
                      definitions={definitions}
                    />
                    <ModalChipRow
                      title="Strategy"
                      labels={(strategy || [])
                        .filter((opt) => {
                          return !STRATEGY_PARENT_OPTIONS.includes(
                            `strategy:${opt}`
                          )
                        })
                        .map(
                          (opt) =>
                            definitions.find((def) => {
                              return `strategy:${opt}` === def.id
                            })?.display_label || ''
                        )}
                      definitions={definitions}
                    />
                    <ModalChipRow
                      wrap
                      title="Liable Institution"
                      labels={(target || [])
                        .filter((opt) => {
                          return !TARGET_PARENT_OPTIONS.includes(
                            `target:${opt}`
                          )
                        })
                        .map(
                          (opt) =>
                            definitions.find((def) => {
                              return `target:${opt}` === def.id
                            })?.display_label || ''
                        )}
                      definitions={definitions}
                    />
                  </>
                ) : (
                  <>
                    <ModalChipRow
                      title="Geo Scope"
                      labels={(geo_scope || []).map(
                        (opt) =>
                          definitions.find((def) => {
                            return `geo_scope:${opt}` === def.id
                          })?.display_label || ''
                      )}
                      definitions={definitions}
                    />
                    <ModalChipRow
                      title="Stage"
                      labels={(stage || []).map(
                        (opt) =>
                          definitions.find((def) => {
                            return `stage:${opt}` === def.id
                          })?.display_label || ''
                      )}
                      definitions={definitions}
                    />
                    <ModalChipRow
                      wrap
                      title="Form of Repair"
                      labels={(repair || []).map(
                        (opt) =>
                          definitions.find((def) => {
                            return `repair:${opt}` === def.id
                          })?.display_label || ''
                      )}
                      definitions={definitions}
                    />
                    <ModalChipRow
                      wrap
                      title="Type of Initiative"
                      labels={(initiative_type || []).map(
                        (opt) =>
                          definitions.find((def) => {
                            return `initiative_type:${opt}` === def.id
                          })?.display_label || ''
                      )}
                      definitions={definitions}
                    />

                    <div className="flex items-start gap-6">
                      <p
                        className={
                          'uppercase text-[12px] font-decimal min-w-[100px] max-w-[100px]'
                        }
                      >
                        Budget Allocated
                      </p>
                      <p className="text-[12px] font-semibold">
                        {budget_amount}{' '}
                        <span className="font-normal">as of</span> {budget_date}
                      </p>
                    </div>

                    <div className="flex items-start gap-6">
                      <p
                        className={
                          'uppercase text-[12px] font-decimal min-w-[100px] max-w-[100px]'
                        }
                      >
                        Disbursement
                      </p>
                      <p className="text-[12px] font-semibold">
                        {value_amount}{' '}
                        <span className="font-normal">as of</span> {value_date}
                      </p>
                    </div>
                  </>
                )}
              </Stack>
            </Stack>
            {organization.org_about && (
              <p className="font-decimal text-[14px] text-black">
                {organization.org_about}
              </p>
            )}
          </Stack>
          <Stack className="gap-8">
            <div className="flex flex-col">
              <div className="flex mb-16 w-[300px] h-[300px]">
                {organization.state && <State organization={organization} />}
              </div>
              {organization.state && (
                <p className="font-decimal text-[14px] text-black uppercase mb-4">
                  {`${organization.city}, ${organization.state}`}
                </p>
              )}
              <Link href={organization.website} className="mb-3">
                {organization.website}
              </Link>
              {/* {organization.email && (
                <p className="font-decimal text-[12px] text-black">
                  {organization.email}
                </p>
              )} */}
            </div>
            <img
              src={RedLine}
              alt="Red squiggly line"
              className="w-[55px] h-[8px]"
            />
            {currentIndex < cityOrganizations.length - 1 && (
              <p className="text-[12px] font-decimal">
                Check out{' '}
                <Link
                  className="cursor-pointer"
                  onClick={() => {
                    onOrganizationClick(
                      cityOrganizations[currentIndex + 1],
                      cityOrganizations
                    )
                  }}
                >
                  {cityOrganizations[currentIndex + 1].org_name}
                </Link>
              </p>
            )}
          </Stack>
        </Stack>
      </div>
    </Modal>
  )
}
export default DetailsModal
